// ===== Custom canvas chart: candles/bars/line, price axis on RIGHT, sub-windows, crosshair, drawings =====
import { useEffect, useRef } from "react";
import { getSeries, getState, onChange, TF_MS, TF_LIST, setChart, update, addIndicator, setUI, chartOf } from "./store";

const DASHES = { solid: [], dash: [6, 3], dot: [1, 2] };
import { computeIndicator } from "./indicators";
import { openMenu } from "./ContextMenu";
import OneClick from "./OneClick";

const AXIS_W = 62, TIME_H = 18, SUB_H = 110;
const DRAW_TOOLS = ["hline", "vline", "trend", "rect", "fib"];
const FIB = [0, 0.236, 0.382, 0.5, 0.618, 1, 1.618];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const THEMES = {
  dark: { bg: "#1E1E1E", grid: "#333333", text: "#D4D4D4", axis: "#A0A0A0", border: "#4A4A4A", bull: "#00C853", bear: "#FF1744", line: "#00C853", cross: "#B0B0B0", tipBg: "#2D2D30", draw: "#FFB300", wm: "rgba(255,255,255,0.05)" },
  light: { bg: "#FFFFFF", grid: "#D9D9D9", text: "#000000", axis: "#000000", border: "#A0A0A0", bull: "#00C853", bear: "#FF1744", line: "#000000", cross: "#606060", tipBg: "#FFFFE1", draw: "#D50000", wm: "rgba(0,0,0,0.05)" },
};

// Registry of chart views so the toolbar can zoom the active chart
export const chartViews = {};
export const zoomChart = (id, f) => {
  const v = chartViews[id];
  if (!v) return;
  v.spacing = Math.max(1.5, Math.min(64, v.spacing * f));
  v.dirty = true;
};

export const deleteSelected = (id) => {
  const v = chartViews[id];
  if (!v || !v.selected) return false;
  const sel = v.selected;
  v.selected = null;
  update(() => { const c = chartOf(id); c.drawings = c.drawings.filter((d) => d.id !== sel); });
  return true;
};

// Returns { d, part } for the drawing under (x,y) — part is a handle name or "body"
const hitTest = (chart, L, x, y) => {
  const near = (ax, ay, r = 6) => Math.abs(ax - x) <= r && Math.abs(ay - y) <= r;
  const segDist = (x1, y1, x2, y2) => {
    const dx = x2 - x1, dy = y2 - y1, len = dx * dx + dy * dy || 1;
    const t = Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / len));
    return Math.hypot(x - (x1 + t * dx), y - (y1 + t * dy));
  };
  for (let i = chart.drawings.length - 1; i >= 0; i--) {
    const d = chart.drawings[i];
    const x1 = L.t2x(d.t1), y1 = L.p2y(d.p1), x2 = L.t2x(d.t2 ?? d.t1), y2 = L.p2y(d.p2 ?? d.p1);
    if (d.type === "hline" && Math.abs(y - y1) <= 4) return { d, part: "body" };
    if (d.type === "vline" && Math.abs(x - x1) <= 4) return { d, part: "body" };
    if (d.type === "trend" || d.type === "fib") {
      if (near(x1, y1)) return { d, part: "p1" };
      if (near(x2, y2)) return { d, part: "p2" };
      if (segDist(x1, y1, x2, y2) <= 4) return { d, part: "body" };
      if (d.type === "fib" && x >= Math.min(x1, x2) && FIB.some((f) => Math.abs(L.p2y(d.p2 + (d.p1 - d.p2) * f) - y) <= 3)) return { d, part: "body" };
    }
    if (d.type === "rect") {
      if (near(x1, y1)) return { d, part: "p1" };
      if (near(x2, y2)) return { d, part: "p2" };
      if (near(x1, y2)) return { d, part: "c12" };
      if (near(x2, y1)) return { d, part: "c21" };
      if (x >= Math.min(x1, x2) - 3 && x <= Math.max(x1, x2) + 3 && y >= Math.min(y1, y2) - 3 && y <= Math.max(y1, y2) + 3) return { d, part: "body" };
    }
  }
  return null;
};

const niceStep = (range, target) => {
  const raw = range / target;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const r = raw / mag;
  return (r < 1.5 ? 1 : r < 3 ? 2 : r < 7 ? 5 : 10) * mag;
};
const pad2 = (n) => String(n).padStart(2, "0");
const timeLabel = (t, tf) => {
  const d = new Date(t);
  const day = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return ["D1", "W1", "MN"].includes(tf) ? `${day} ${d.getFullYear()}` : `${day} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const fullTime = (t) => {
  const d = new Date(t);
  return `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

// ---------- Rendering ----------
function draw(ctx, W, H, view, chart) {
  const st = getState();
  const C = THEMES[st.theme];
  const sym = st.symbols[chart.symbol];
  const dg = sym.digits;
  const bars = getSeries(chart.symbol, chart.tf);
  const n = bars.length;
  const ms = TF_MS[chart.tf];
  if (!chart.autoScroll && view.lastN && n > view.lastN) view.offset += n - view.lastN;
  view.lastN = n;

  ctx.font = '10px Tahoma, "Segoe UI", sans-serif';
  ctx.textBaseline = "middle";
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, W, H);

  const plotW = W - AXIS_W;
  const plotH = H - TIME_H;
  const sp = view.spacing;
  const right = n - 1 - view.offset;
  const idx2x = (i) => plotW - (right - i) * sp - sp / 2;
  const first = Math.max(0, Math.floor(right - plotW / sp));
  const last = Math.min(n - 1, Math.ceil(right));

  const comp = chart.indicators.map((ind) => ({ ind, ...computeIndicator(ind, bars) }));
  const subs = comp.filter((c) => !c.overlay);
  if (chart.volumes) subs.unshift({ vol: true, label: "Volumes" });
  const subH = subs.length ? Math.min(SUB_H, (plotH * 0.5) / subs.length) : 0;
  const mainH = plotH - subs.length * subH;

  // main price range (includes overlays)
  let lo = Infinity, hi = -Infinity;
  for (let i = first; i <= last; i++) {
    lo = Math.min(lo, chart.type === "line" ? bars[i].c : bars[i].l);
    hi = Math.max(hi, chart.type === "line" ? bars[i].c : bars[i].h);
    comp.forEach((c) => c.overlay && c.lines.forEach((l) => { const v = l.values[i]; if (v != null) { lo = Math.min(lo, v); hi = Math.max(hi, v); } }));
  }
  if (!isFinite(lo)) { lo = sym.bid * 0.99; hi = sym.bid * 1.01; }
  const padP = (hi - lo || sym.point * 10) * 0.08;
  lo -= padP; hi += padP;
  const p2y = (p) => 4 + ((hi - p) / (hi - lo)) * (mainH - 8);
  const y2p = (y) => hi - ((y - 4) / (mainH - 8)) * (hi - lo);
  const x2t = (x) => timeAt(bars, right - (plotW - x - sp / 2) / sp, ms);
  const t2x = (t) => idx2x(idxAt(bars, t, ms));
  view.layout = { plotW, plotH, mainH, subH, subs, y2p, x2t, p2y, t2x, right, first, last };

  // grid & time scale
  const vStep = Math.max(1, Math.ceil(90 / sp));
  ctx.setLineDash([1, 2]);
  ctx.strokeStyle = C.grid;
  ctx.lineWidth = 1;
  const timeMarks = [];
  for (let i = first - (first % vStep); i <= last; i += vStep) {
    if (i < 0) continue;
    const x = Math.round(idx2x(i)) + 0.5;
    if (x > plotW) continue;
    timeMarks.push([x, bars[i].t]);
    if (chart.grid) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, plotH); ctx.stroke(); }
  }
  const pStep = niceStep(hi - lo, Math.max(2, mainH / 40));
  const priceMarks = [];
  for (let p = Math.ceil(lo / pStep) * pStep; p <= hi; p += pStep) {
    const y = Math.round(p2y(p)) + 0.5;
    priceMarks.push([y, p]);
    if (chart.grid) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(plotW, y); ctx.stroke(); }
  }
  ctx.setLineDash([]);

  // watermark (large symbol name)
  ctx.save();
  ctx.fillStyle = C.wm;
  ctx.font = 'bold 48px Tahoma, "Segoe UI", sans-serif';
  ctx.textAlign = "center";
  ctx.fillText(`${chart.symbol},${chart.tf}`, plotW / 2, mainH / 2);
  ctx.restore();

  // price bars
  ctx.save();
  ctx.beginPath(); ctx.rect(0, 0, plotW, mainH); ctx.clip();
  const bw = Math.max(1, Math.floor(sp * 0.6));
  if (chart.type === "line") {
    ctx.strokeStyle = C.line; ctx.lineWidth = 1; ctx.beginPath();
    for (let i = first; i <= last; i++) { const x = idx2x(i), y = p2y(bars[i].c); i === first ? ctx.moveTo(x, y) : ctx.lineTo(x, y); }
    ctx.stroke();
  } else {
    for (let i = first; i <= last; i++) {
      const b = bars[i];
      const x = Math.round(idx2x(i)) + 0.5;
      const col = b.c >= b.o ? C.bull : C.bear;
      ctx.strokeStyle = col; ctx.fillStyle = col;
      ctx.beginPath(); ctx.moveTo(x, Math.round(p2y(b.h))); ctx.lineTo(x, Math.round(p2y(b.l)) + 1);
      if (chart.type === "bars") {
        const t = Math.max(2, Math.floor(sp * 0.35));
        ctx.moveTo(x - t, Math.round(p2y(b.o)) + 0.5); ctx.lineTo(x, Math.round(p2y(b.o)) + 0.5);
        ctx.moveTo(x, Math.round(p2y(b.c)) + 0.5); ctx.lineTo(x + t + 1, Math.round(p2y(b.c)) + 0.5);
      }
      ctx.stroke();
      if (chart.type === "candles" && bw >= 2) {
        const y1 = Math.round(p2y(Math.max(b.o, b.c))), y2 = Math.round(p2y(Math.min(b.o, b.c)));
        ctx.fillRect(Math.round(x - bw / 2), y1, bw, Math.max(1, y2 - y1));
      }
    }
  }
  // overlays (MA, EMA, Bands)
  comp.filter((c) => c.overlay).forEach((c) => c.lines.forEach((l) => drawLine(ctx, l, first, last, idx2x, p2y)));
  ctx.restore();

  // position / order / SL / TP lines
  const priceTag = [];
  ctx.save();
  ctx.setLineDash([4, 3]);
  const trades = [...st.positions.map((p) => ({ ...p, kind: p.type })), ...st.orders.map((o) => ({ ...o, kind: o.type.replace("_", " ") }))].filter((p) => p.symbol === chart.symbol);
  trades.forEach((p) => {
    const lines = [[p.price, "#00C853", `#${p.ticket} ${p.kind} ${p.volume.toFixed(2)}`]];
    if (p.sl) lines.push([p.sl, "#FF1744", `#${p.ticket} sl`]);
    if (p.tp) lines.push([p.tp, "#FF1744", `#${p.ticket} tp`]);
    lines.forEach(([pr, col, label]) => {
      const y = Math.round(p2y(pr)) + 0.5;
      if (y < 0 || y > mainH) return;
      ctx.strokeStyle = col; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(plotW, y); ctx.stroke();
      ctx.fillStyle = col; ctx.textAlign = "left"; ctx.fillText(label, 4, y - 7);
      priceTag.push([y, pr, col]);
    });
  });
  ctx.restore();

  // price alert levels
  ctx.save();
  ctx.setLineDash([2, 4]); ctx.strokeStyle = "#FFB300"; ctx.fillStyle = "#FFB300"; ctx.textAlign = "right";
  st.alerts.filter((a) => a.enabled && a.symbol === chart.symbol && (!a.kind || a.kind === "price")).forEach((a) => {
    const y = Math.round(p2y(a.value)) + 0.5;
    if (y < 0 || y > mainH) return;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(plotW, y); ctx.stroke();
    ctx.fillText(`alert ${a.cond} ${a.value}`, plotW - 4, y - 7);
  });
  ctx.restore();

  // drawings (objects)
  [...chart.drawings, ...(view.draft ? [view.draft] : [])].forEach((d) => drawObject(ctx, d, { t2x, p2y, plotW, mainH, dg, C, priceTag, selected: d.id === view.selected }));

  // bid price line
  const by = Math.round(p2y(sym.bid)) + 0.5;
  ctx.setLineDash([3, 3]); ctx.strokeStyle = "#A0A0A0";
  ctx.beginPath(); ctx.moveTo(0, by); ctx.lineTo(plotW, by); ctx.stroke();
  ctx.setLineDash([]);

  // sub-windows
  subs.forEach((s, k) => drawSub(ctx, s, k, { top: mainH + k * subH, h: subH, plotW, W, first, last, idx2x, bars, C, view }));

  // right price axis
  ctx.fillStyle = C.bg; ctx.fillRect(plotW, 0, AXIS_W, H);
  ctx.strokeStyle = C.border; ctx.beginPath(); ctx.moveTo(plotW + 0.5, 0); ctx.lineTo(plotW + 0.5, plotH); ctx.lineTo(0, plotH + 0.5); ctx.stroke();
  ctx.fillStyle = C.axis; ctx.textAlign = "left";
  priceMarks.forEach(([y, p]) => {
    if (y < 6 || y > mainH - 6) return;
    ctx.fillRect(plotW, y, 4, 1);
    ctx.fillText(p.toFixed(dg), plotW + 7, y);
  });
  subs.forEach((s) => s.axis && s.axis.forEach(([y, label]) => { ctx.fillStyle = C.axis; ctx.fillRect(plotW, y, 4, 1); ctx.fillText(label, plotW + 7, y); }));
  priceTag.forEach(([y, p, col]) => axisTag(ctx, plotW, y, p.toFixed(dg), col, "#fff"));
  const lastBar = bars[n - 1];
  axisTag(ctx, plotW, by, sym.bid.toFixed(dg), lastBar.c >= lastBar.o ? C.bull : C.bear, "#000");

  // bottom time axis
  ctx.fillStyle = C.axis; ctx.textAlign = "left";
  timeMarks.forEach(([x, t]) => { ctx.fillRect(x, plotH, 1, 4); ctx.fillText(timeLabel(t, chart.tf), x + 2, plotH + 10); });

  // header text: symbol,timeframe + description, then OHLC
  const m = view.mouse;
  const hIdx = m && m.x < plotW ? Math.round(right - (plotW - m.x - sp / 2) / sp) : n - 1;
  const hb = bars[Math.max(0, Math.min(n - 1, hIdx))];
  ctx.textAlign = "left"; ctx.fillStyle = C.text;
  ctx.font = 'bold 11px "Segoe UI", Tahoma, sans-serif';
  ctx.fillText(`${chart.symbol},${chart.tf}`, 6, 11);
  const w0 = ctx.measureText(`${chart.symbol},${chart.tf}`).width;
  ctx.font = '11px "Segoe UI", Tahoma, sans-serif';
  ctx.fillText(`:  ${sym.desc}`, 6 + w0, 11);
  ctx.fillText(`${hb.o.toFixed(dg)}  ${hb.h.toFixed(dg)}  ${hb.l.toFixed(dg)}  ${hb.c.toFixed(dg)}`, 6, 25);
  ctx.font = '10px Tahoma, "Segoe UI", sans-serif';
  let ox = 6;
  comp.filter((c) => c.overlay).forEach((c) => { ctx.fillStyle = c.ind.color; const t = c.label; ctx.fillText(t, ox + 270, 25); ox += ctx.measureText(t).width + 10; });

  // crosshair + OHLC tooltip
  if (m && m.x < plotW && m.y < plotH && !view.draft) {
    const cx = Math.round(idx2x(hIdx)) + 0.5, cy = Math.round(m.y) + 0.5;
    ctx.strokeStyle = C.cross; ctx.setLineDash([1, 2]);
    ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, plotH); ctx.moveTo(0, cy); ctx.lineTo(plotW, cy); ctx.stroke();
    ctx.setLineDash([]);
    const priceTxt = m.y < mainH ? y2p(m.y).toFixed(dg) : "";
    if (priceTxt) axisTag(ctx, plotW, cy, priceTxt, C.cross, "#000");
    const tTxt = fullTime(timeAt(bars, hIdx, ms));
    const tw = ctx.measureText(tTxt).width + 8;
    ctx.fillStyle = C.cross; ctx.fillRect(cx - tw / 2, plotH + 1, tw, TIME_H - 2);
    ctx.fillStyle = "#000"; ctx.textAlign = "center"; ctx.fillText(tTxt, cx, plotH + TIME_H / 2);
    if (hIdx >= 0 && hIdx < n) {
      const rows = [fullTime(hb.t), `Open:   ${hb.o.toFixed(dg)}`, `High:   ${hb.h.toFixed(dg)}`, `Low:    ${hb.l.toFixed(dg)}`, `Close:  ${hb.c.toFixed(dg)}`, `Volume: ${hb.v}`];
      let tx = m.x + 16, ty = m.y + 16;
      if (tx + 130 > plotW) tx = m.x - 146;
      if (ty + 86 > plotH) ty = m.y - 96;
      ctx.fillStyle = C.tipBg; ctx.strokeStyle = C.border;
      ctx.fillRect(tx, ty, 130, 82); ctx.strokeRect(tx + 0.5, ty + 0.5, 130, 82);
      ctx.fillStyle = C.text; ctx.textAlign = "left";
      rows.forEach((r, i) => ctx.fillText(r, tx + 6, ty + 9 + i * 13));
    }
    window.dispatchEvent(new CustomEvent("mt5hover", { detail: { bar: hb, dg } }));
  }
}

function drawLine(ctx, l, first, last, idx2x, p2y) {
  ctx.strokeStyle = l.color; ctx.lineWidth = 1;
  ctx.setLineDash(l.dash ? [2, 2] : []);
  ctx.beginPath();
  let started = false;
  for (let i = first; i <= last; i++) {
    const v = l.values[i];
    if (v == null) continue;
    const x = idx2x(i), y = p2y(v);
    started ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
    started = true;
  }
  ctx.stroke();
  ctx.setLineDash([]);
}

function axisTag(ctx, plotW, y, txt, bg, fg) {
  ctx.fillStyle = bg;
  ctx.fillRect(plotW + 1, y - 7, AXIS_W - 1, 14);
  ctx.fillStyle = fg; ctx.textAlign = "left";
  ctx.fillText(txt, plotW + 7, y);
}

// Bar index <-> time using real bar timestamps (handles weekend gaps), extrapolating past either end
const timeAt = (bars, i, ms) => {
  const n = bars.length;
  if (i <= 0) return bars[0].t + i * ms;
  if (i >= n - 1) return bars[n - 1].t + (i - n + 1) * ms;
  const k = Math.floor(i);
  return bars[k].t + (i - k) * (bars[k + 1].t - bars[k].t);
};
const idxAt = (bars, t, ms) => {
  const n = bars.length;
  if (t <= bars[0].t) return (t - bars[0].t) / ms;
  if (t >= bars[n - 1].t) return n - 1 + (t - bars[n - 1].t) / ms;
  let lo = 0, hi = n - 1;
  while (hi - lo > 1) { const m = (lo + hi) >> 1; if (bars[m].t <= t) lo = m; else hi = m; }
  return lo + (t - bars[lo].t) / (bars[hi].t - bars[lo].t);
};

function drawObject(ctx, d, { t2x, p2y, plotW, mainH, dg, C, priceTag, selected }) {
  const col = d.color || C.draw;
  ctx.save();
  ctx.beginPath(); ctx.rect(0, 0, plotW, mainH); ctx.clip();
  ctx.strokeStyle = col; ctx.fillStyle = col; ctx.lineWidth = d.width || 1;
  ctx.setLineDash(DASHES[d.style || "solid"]);
  const x1 = t2x(d.t1), y1 = p2y(d.p1), x2 = t2x(d.t2 ?? d.t1), y2 = p2y(d.p2 ?? d.p1);
  ctx.beginPath();
  if (d.type === "hline") { ctx.moveTo(0, Math.round(y1) + 0.5); ctx.lineTo(plotW, Math.round(y1) + 0.5); priceTag.push([Math.round(y1) + 0.5, d.p1, col]); }
  if (d.type === "vline") { ctx.moveTo(Math.round(x1) + 0.5, 0); ctx.lineTo(Math.round(x1) + 0.5, mainH); }
  if (d.type === "trend") { ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); ctx.fillRect(x1 - 2, y1 - 2, 4, 4); ctx.fillRect(x2 - 2, y2 - 2, 4, 4); }
  if (d.type === "rect") {
    ctx.globalAlpha = 0.18; ctx.fillRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
    ctx.globalAlpha = 1; ctx.strokeRect(Math.min(x1, x2) + 0.5, Math.min(y1, y2) + 0.5, Math.abs(x2 - x1), Math.abs(y2 - y1));
  }
  if (d.type === "fib") {
    ctx.save(); ctx.setLineDash([2, 2]); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); ctx.restore();
    ctx.textAlign = "left"; ctx.textBaseline = "bottom";
    FIB.forEach((f) => {
      const p = d.p2 + (d.p1 - d.p2) * f;
      const y = Math.round(p2y(p)) + 0.5;
      ctx.beginPath(); ctx.moveTo(Math.min(x1, x2), y); ctx.lineTo(plotW, y); ctx.stroke();
      ctx.fillText(`${(f * 100).toFixed(1)}  ${p.toFixed(dg)}`, Math.min(x1, x2) + 2, y - 1);
    });
    ctx.textBaseline = "middle";
  }
  ctx.stroke();
  // selection handles
  if (selected) {
    ctx.setLineDash([]);
    const pts = d.type === "hline" ? [[plotW / 2, y1]] : d.type === "vline" ? [[x1, mainH / 2]] : d.type === "rect" ? [[x1, y1], [x2, y2], [x1, y2], [x2, y1]] : [[x1, y1], [x2, y2]];
    pts.forEach(([hx, hy]) => { ctx.fillStyle = C.bg; ctx.fillRect(hx - 3, hy - 3, 7, 7); ctx.strokeStyle = col; ctx.lineWidth = 1; ctx.strokeRect(hx - 2.5, hy - 2.5, 6, 6); });
  }
  ctx.restore();
}

function drawSub(ctx, s, k, { top, h, plotW, first, last, idx2x, bars, C, view }) {
  let lo, hi;
  if (s.vol) { lo = 0; hi = 1; for (let i = first; i <= last; i++) hi = Math.max(hi, bars[i].v); }
  else if (s.range) [lo, hi] = s.range;
  else {
    lo = Infinity; hi = -Infinity;
    for (let i = first; i <= last; i++) s.lines.forEach((l) => { const v = l.values[i]; if (v != null) { lo = Math.min(lo, v); hi = Math.max(hi, v); } });
    if (!isFinite(lo)) { lo = -1; hi = 1; }
    const pd = (hi - lo) * 0.1 || 1; lo -= pd; hi += pd;
  }
  const p2y = (p) => top + 14 + ((hi - p) / (hi - lo || 1)) * (h - 18);
  ctx.save();
  ctx.beginPath(); ctx.rect(0, top, plotW, h); ctx.clip();
  ctx.fillStyle = C.bg; ctx.fillRect(0, top, plotW, h);
  ctx.strokeStyle = C.border; ctx.beginPath(); ctx.moveTo(0, top + 0.5); ctx.lineTo(plotW + AXIS_W, top + 0.5); ctx.stroke();
  const sp = view.spacing;
  const bw = Math.max(1, Math.floor(sp * 0.6));
  if (s.vol) {
    for (let i = first; i <= last; i++) {
      const b = bars[i], x = Math.round(idx2x(i));
      ctx.fillStyle = b.c >= b.o ? C.bull : C.bear;
      const y = Math.round(p2y(b.v));
      ctx.fillRect(x - Math.floor(bw / 2), y, bw, top + h - y);
    }
  } else {
    (s.levels || []).forEach((lv) => {
      const y = Math.round(p2y(lv)) + 0.5;
      ctx.setLineDash([1, 2]); ctx.strokeStyle = C.axis; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(plotW, y); ctx.stroke(); ctx.setLineDash([]);
    });
    s.lines.forEach((l) => {
      if (!l.hist) return drawLine(ctx, l, first, last, idx2x, p2y);
      const y0 = Math.round(p2y(0));
      ctx.fillStyle = l.color;
      for (let i = first; i <= last; i++) { const v = l.values[i]; if (v == null) continue; const y = Math.round(p2y(v)); ctx.fillRect(Math.round(idx2x(i)) - Math.floor(bw / 2), Math.min(y, y0), bw, Math.max(1, Math.abs(y - y0))); }
    });
  }
  // label with last value
  const li = Math.min(last, bars.length - 1);
  const vals = s.vol ? [bars[li].v] : s.lines.map((l) => l.values[li]).filter((v) => v != null).map((v) => v.toFixed(Math.abs(v) < 1 ? 5 : 2));
  ctx.textAlign = "left"; ctx.fillStyle = C.text;
  ctx.fillText(`${s.label}  ${vals.join("  ")}`, 6, top + 9);
  ctx.restore();
  const marks = s.levels ? [...s.levels] : [lo + (hi - lo) * 0.15, hi - (hi - lo) * 0.15];
  s.axis = marks.map((v) => [Math.round(p2y(v)) + 0.5, s.vol ? Math.round(v).toString() : v.toFixed(Math.abs(v) < 1 && v !== 0 ? 5 : 2)]);
}

// ---------- React component ----------
export default function Chart({ chartId }) {
  const wrap = useRef(null);
  const cv = useRef(null);
  const v = useRef({ spacing: 8, offset: -6, mouse: null, drag: null, draft: null, edit: null, selected: null, dirty: true, layout: null });

  useEffect(() => {
    const view = v.current;
    chartViews[chartId] = view;
    const canvas = cv.current;
    const ctx = canvas.getContext("2d");
    let W = 0, H = 0, raf;
    const resize = () => {
      const r = wrap.current.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      W = Math.max(50, r.width); H = Math.max(50, r.height);
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = `${W}px`; canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      view.dirty = true;
    };
    const ro = new ResizeObserver(resize);
    ro.observe(wrap.current);
    resize();
    const unsub = onChange(() => (view.dirty = true));
    const loop = () => {
      if (view.dirty) { view.dirty = false; draw(ctx, W, H, view, chartOf(chartId)); }
      raf = requestAnimationFrame(loop);
    };
    loop();
    const wheel = (e) => { e.preventDefault(); zoomChart(chartId, e.deltaY < 0 ? 1.15 : 1 / 1.15); };
    canvas.addEventListener("wheel", wheel, { passive: false });
    return () => { ro.disconnect(); unsub(); cancelAnimationFrame(raf); canvas.removeEventListener("wheel", wheel); delete chartViews[chartId]; };
  }, [chartId]);

  const pos = (e) => { const r = cv.current.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };

  const onDown = (e) => {
    if (e.button !== 0) return;
    const view = v.current, L = view.layout, { x, y } = pos(e);
    const tool = getState().tool;
    if (getState().activeChart !== chartId) setUI({ activeChart: chartId });
    if (!DRAW_TOOLS.includes(tool) && L && y < L.mainH && x < L.plotW) {
      const hit = hitTest(chartOf(chartId), L, x, y);
      view.selected = hit ? hit.d.id : null;
      view.dirty = true;
      if (hit) { view.edit = { ...hit, t: L.x2t(x), p: L.y2p(y), orig: { ...hit.d } }; return; }
    }
    if (DRAW_TOOLS.includes(tool) && L && y < L.mainH && x < L.plotW) {
      const pt = { t1: L.x2t(x), p1: L.y2p(y) };
      if (tool === "hline" || tool === "vline") {
        update(() => chartOf(chartId).drawings.push({ id: Date.now(), type: tool, ...pt }));
        setUI({ tool: "cursor" });
      } else view.draft = { id: Date.now(), type: tool, ...pt, t2: pt.t1, p2: pt.p1 };
    } else view.drag = { x, offset: view.offset };
  };
  const onMove = (e) => {
    const view = v.current, L = view.layout, p = pos(e);
    view.mouse = p;
    if (view.drag) view.offset = view.drag.offset + (p.x - view.drag.x) / view.spacing;
    if (view.edit && L) {
      const { d, part, orig } = view.edit;
      const t = L.x2t(p.x), pr = L.y2p(Math.min(p.y, L.mainH));
      const dt = t - view.edit.t, dp = pr - view.edit.p;
      if (part === "body") { d.t1 = orig.t1 + dt; d.p1 = orig.p1 + dp; if (orig.t2 != null) { d.t2 = orig.t2 + dt; d.p2 = orig.p2 + dp; } }
      if (part === "p1") { d.t1 = t; d.p1 = pr; }
      if (part === "p2") { d.t2 = t; d.p2 = pr; }
      if (part === "c12") { d.t1 = t; d.p2 = pr; }
      if (part === "c21") { d.t2 = t; d.p1 = pr; }
      view.moved = true;
    } else if (L && !view.drag && !view.draft && p.x < L.plotW && p.y < L.mainH && !DRAW_TOOLS.includes(getState().tool)) {
      const hit = hitTest(chartOf(chartId), L, p.x, p.y);
      cv.current.style.cursor = hit ? (hit.part === "body" ? "move" : "pointer") : getState().tool === "crosshair" ? "crosshair" : "default";
    }
    if (view.draft && L) { view.draft.t2 = L.x2t(p.x); view.draft.p2 = L.y2p(Math.min(p.y, L.mainH)); }
    view.dirty = true;
  };
  const onUp = () => {
    const view = v.current;
    if (view.draft) {
      const d = view.draft;
      view.draft = null;
      if (d.t1 !== d.t2 || d.p1 !== d.p2) update(() => chartOf(chartId).drawings.push(d));
      setUI({ tool: "cursor" });
    }
    if (view.edit) { const m = view.moved; view.edit = null; view.moved = false; if (m) update(() => {}); }
    view.drag = null;
  };
  const onDbl = (e) => {
    const L = v.current.layout, { x, y } = pos(e);
    const hit = L && hitTest(chartOf(chartId), L, x, y);
    if (hit) setUI({ dialog: { kind: "object", chartId, id: hit.d.id } });
  };
  const onLeave = () => { onUp(); v.current.mouse = null; v.current.dirty = true; };

  const onDrop = (e) => {
    e.preventDefault();
    try {
      const d = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (d.kind === "symbol") setChart({ symbol: d.value }, chartId);
      if (d.kind === "indicator") addIndicator(d.value, chartId);
    } catch (err) { /* ignore foreign drops */ }
  };

  const onContext = (e) => {
    const c = chartOf(chartId);
    const L = v.current.layout;
    const { y } = pos(e);
    const st = getState();
    const sym = st.symbols[c.symbol];
    const price = L && y < L.mainH ? +L.y2p(y).toFixed(sym.digits) : sym.bid;
    const hit = L && hitTest(c, L, pos(e).x, y);
    if (hit) {
      v.current.selected = hit.d.id; v.current.dirty = true;
      return openMenu(e, [
        { label: "Properties...", onClick: () => setUI({ dialog: { kind: "object", chartId, id: hit.d.id } }) },
        { label: "Color", items: ["#FFB300", "#FF1744", "#00C853", "#1E88E5", "#FFFFFF", "#9C27B0"].map((col) => ({ label: col, icon: <span style={{ display: "inline-block", width: 10, height: 10, background: col }} />, onClick: () => update(() => { hit.d.color = col; }) })) },
        { label: "Width", items: [1, 2, 3, 4].map((w) => ({ label: `${w}px`, checked: (hit.d.width || 1) === w, onClick: () => update(() => { hit.d.width = w; }) })) },
        { label: "Style", items: ["solid", "dash", "dot"].map((st2) => ({ label: st2, checked: (hit.d.style || "solid") === st2, onClick: () => update(() => { hit.d.style = st2; }) })) },
        { sep: true },
        { label: "Delete", shortcut: "Delete", onClick: () => deleteSelected(chartId) },
        { label: "Delete All Objects", onClick: () => setChart({ drawings: [] }, chartId) },
      ]);
    }
    if (st.activeChart !== chartId) setUI({ activeChart: chartId });
    const pend = (type) => setUI({ dialog: { kind: "order", symbol: c.symbol, mode: "pending", pendingType: type, price } });
    const above = price > sym.ask;
    openMenu(e, [
      { label: "Trading", items: [
        { label: `Buy ${sym.ask.toFixed(sym.digits)}`, onClick: () => setUI({ dialog: { kind: "order", symbol: c.symbol, mode: "market" } }) },
        { label: `Sell ${sym.bid.toFixed(sym.digits)}`, onClick: () => setUI({ dialog: { kind: "order", symbol: c.symbol, mode: "market" } }) },
        { sep: true },
        { label: `${above ? "Sell Limit" : "Buy Limit"} at ${price}`, onClick: () => pend(above ? "sell_limit" : "buy_limit") },
        { label: `${above ? "Buy Stop" : "Sell Stop"} at ${price}`, onClick: () => pend(above ? "buy_stop" : "sell_stop") },
        { sep: true },
        { label: "New Order", shortcut: "F9", onClick: () => setUI({ dialog: { kind: "order", symbol: c.symbol, mode: "market" } }) },
      ] },
      { label: `Create Alert at ${price}`, onClick: () => setUI({ dialog: { kind: "alert", symbol: c.symbol, value: price, cond: price > sym.bid ? "Bid >" : "Bid <" } }) },
      { label: "Create Indicator Alert...", onClick: () => setUI({ dialog: { kind: "alert", alertKind: "rsi", symbol: c.symbol, tf: c.tf } }) },
      { label: "One Click Trading", shortcut: "Alt+T", checked: c.oneClick, onClick: () => setChart({ oneClick: !c.oneClick }, chartId) },
      { sep: true },
      { label: "Depth Of Market", shortcut: "Alt+B", onClick: () => setUI({ dom: c.symbol }) },
      { label: "Indicators List", shortcut: "Ctrl+I", onClick: () => setUI({ dialog: { kind: "indicators", chartId } }) },
      { label: "Insert Indicator", items: ["SMA", "EMA", "BB", "RSI", "MACD", "STOCH"].map((t) => ({ label: { SMA: "Moving Average", EMA: "Exponential MA", BB: "Bollinger Bands", RSI: "Relative Strength Index", MACD: "MACD", STOCH: "Stochastic Oscillator" }[t], onClick: () => addIndicator(t, chartId) })) },
      { label: "Objects List", items: [
        { label: `Delete All Objects (${c.drawings.length})`, onClick: () => setChart({ drawings: [] }, chartId) },
        { label: "Delete Last Object", shortcut: "Ctrl+Z", onClick: () => setChart({ drawings: c.drawings.slice(0, -1) }, chartId) },
      ] },
      { sep: true },
      { label: "Template", items: [{ label: "Save Template...", disabled: true }, { label: "Default", onClick: () => setChart({ indicators: [], drawings: [], type: "candles", grid: true, volumes: false }, chartId) }] },
      { label: "Refresh", onClick: () => (v.current.dirty = true) },
      { sep: true },
      { label: "Auto Scroll", checked: c.autoScroll, onClick: () => setChart({ autoScroll: !c.autoScroll }, chartId) },
      { label: "Grid", shortcut: "Ctrl+G", checked: c.grid, onClick: () => setChart({ grid: !c.grid }, chartId) },
      { label: "Volumes", shortcut: "Ctrl+K", checked: c.volumes, onClick: () => setChart({ volumes: !c.volumes }, chartId) },
      { sep: true },
      { label: "Bar Chart", shortcut: "Alt+1", checked: c.type === "bars", onClick: () => setChart({ type: "bars" }, chartId) },
      { label: "Candlesticks", shortcut: "Alt+2", checked: c.type === "candles", onClick: () => setChart({ type: "candles" }, chartId) },
      { label: "Line Chart", shortcut: "Alt+3", checked: c.type === "line", onClick: () => setChart({ type: "line" }, chartId) },
      { label: "Timeframes", items: TF_LIST.map((tf) => ({ label: tf, checked: c.tf === tf, onClick: () => setChart({ tf }, chartId) })) },
      { sep: true },
      { label: "Zoom In", shortcut: "+", onClick: () => zoomChart(chartId, 1.3) },
      { label: "Zoom Out", shortcut: "-", onClick: () => zoomChart(chartId, 1 / 1.3) },
      { sep: true },
      { label: "Save As Picture...", onClick: () => { const a = document.createElement("a"); a.href = cv.current.toDataURL("image/png"); a.download = `${c.symbol}${c.tf}.png`; a.click(); } },
      { label: "Properties", shortcut: "F8", onClick: () => setUI({ dialog: { kind: "properties", chartId } }) },
    ]);
  };

  const tool = getState().tool;
  return (
    <div className="mt-chart" ref={wrap} data-testid={`chart-${chartId}`} onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
      <canvas
        ref={cv}
        data-testid="chart-canvas"
        style={{ cursor: DRAW_TOOLS.includes(tool) || tool === "crosshair" ? "crosshair" : v.current.drag ? "grabbing" : "default" }}
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onLeave} onDoubleClick={onDbl} onContextMenu={onContext}
      />
      <OneClick chartId={chartId} />
    </div>
  );
}
